import {
  useV2ListServiceEstimateAccuraciesQuery,
  V2ListServiceEstimateAccuraciesApiArg,
} from "@generated";
import { AccuracyDirection, ServiceEstimateAccuracy } from "../types";

interface Params {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc" | string;
  /**
   * Which findings to ask for. Filtering server-side rather than dropping rows
   * after the fact keeps paging and `record-count` describing what is actually
   * on screen.
   */
  directions?: AccuracyDirection[];
  skip?: boolean;
}

type SortParam = NonNullable<V2ListServiceEstimateAccuraciesApiArg["sort"]>;

// The endpoint pages like every other v2 index and really does return
// `meta: { record-count, page-count }` - its swagger response block just omits
// the property, unlike its siblings which declare `meta?: V2PagingMeta`. Fixed
// on scopestack-api; delete this once a codegen run picks the correction up.
type PagedResponse = { meta?: { "record-count"?: number; "page-count"?: number } };

// Impact is median-delta-hours x observation-count, and it is the endpoint's
// own default for a reason: ranking on bias alone leads with a 2-hour service
// that is 50% off while a 40-hour service bleeding 12h a delivery sits on
// page three.
const DEFAULT_SORT = ["-impact-hours"] as SortParam;

const useListServiceEstimateAccuracies = ({
  pageNumber,
  pageSize,
  sortBy,
  sortOrder,
  directions,
  skip = false,
}: Params) => {
  let customError: string | null = null;
  let pageMeta = { recordCount: 0, pageCount: 0 };

  const sort = sortBy
    ? ([sortOrder === "asc" ? sortBy : `-${sortBy}`] as SortParam)
    : DEFAULT_SORT;

  const { data, error, isLoading, isFetching, refetch } =
    useV2ListServiceEstimateAccuraciesQuery(
      {
        page: { number: pageNumber, size: pageSize },
        sort,
        ...(directions?.length ? { filter: { direction: directions } } : {}),
      },
      { skip }
    );

  let accuracies: ServiceEstimateAccuracy[] = [];

  if (data?.data) {
    try {
      accuracies = data.data.map((accuracy) => {
        if (!accuracy.id) {
          throw new Error("Missing accuracy.id");
        }

        const attributes = accuracy.attributes;
        const direction = (attributes?.direction ??
          "none") as AccuracyDirection;

        // On a `none` row the API zeroes impact-hours and documents
        // bias-percentage as not actionable, so neither reaches a cell. The
        // observation count still does - "we looked at 14 deliveries and found
        // no consistent bias" is the finding.
        const flagged = direction !== "none";

        return {
          id: String(accuracy.id),
          serviceId: String(attributes?.["service-id"] ?? ""),
          serviceName: attributes?.["service-name"] ?? "",
          direction,
          biasPercentage: flagged
            ? attributes?.["bias-percentage"]
            : undefined,
          impactHours: flagged ? attributes?.["impact-hours"] : undefined,
          observationCount: attributes?.["observation-count"] ?? 0,
          confidence: attributes?.confidence,
          lastDeliveredOn: attributes?.["last-delivered-on"] ?? "",
        };
      });

      const meta = (data as PagedResponse).meta;
      pageMeta = {
        recordCount: meta?.["record-count"] ?? 0,
        pageCount: meta?.["page-count"] ?? 0,
      };
    } catch (err) {
      customError =
        err instanceof Error ? err.message : "An unknown error occurred";
    }
  }

  return {
    accuracies,
    isLoading,
    isFetching,
    error: error ?? customError,
    pageMeta,
    refetch,
  };
};

export default useListServiceEstimateAccuracies;
